import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, Save, Loader2, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  {
    section: 'Employee Portal',
    items: [
      { key: 'portal_new_joiners_days', label: 'New Joining Staff', help: 'Show employees in the portal "New Joiners" widget who joined within this many days.' },
      { key: 'portal_birthdays_days', label: 'Upcoming Birthdays', help: 'Show team birthdays falling within this many days from today.' },
    ],
  },
  {
    section: 'Vynce HRMS Dashboard',
    items: [
      { key: 'dashboard_upcoming_birthdays_days', label: 'Upcoming Birthdays', help: 'Default window for the "Upcoming Birthdays" widget on the main dashboard.' },
      { key: 'dashboard_upcoming_leaves_days', label: 'Upcoming Leaves', help: 'Window for counting future leaves on the dashboard stats card.' },
      { key: 'dashboard_expiring_passport_days', label: 'Expiring Passport', help: 'Window for Passport expiry alerts (also drives the first reminder email).' },
      { key: 'dashboard_expiring_visa_eid_days', label: 'Expiring Visa / Emirates ID', help: 'Window for Visa & Emirates ID expiry alerts (also drives the first reminder email).' },
      { key: 'dashboard_recent_joiners_days', label: 'Recent Joiners', help: 'Window used in HR reports to count "new joiners".' },
    ],
  },
];

const PRESETS = [30, 60, 90];

const ViewWindowsTab = ({ isViewer }) => {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/view-windows`, { headers });
      setCfg(res.data.current);
      setDefaults(res.data.defaults);
    } catch (e) {
      toast({ title: 'Failed to load settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setField = (key, val) => setCfg(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of Object.keys(cfg)) {
        const n = parseInt(cfg[k], 10);
        if (Number.isFinite(n) && n > 0) payload[k] = n;
      }
      await axios.put(`${API}/api/settings/view-windows`, payload, { headers });
      toast({ title: 'View windows saved', description: 'Changes apply immediately across the app.' });
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (!defaults) return;
    setCfg({ ...defaults });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!cfg) return null;

  return (
    <div className="space-y-4" data-testid="view-windows-tab">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-indigo-600" />
            <CardTitle className="text-base">View Window Limits</CardTitle>
          </div>
          <p className="text-xs text-gray-500">Set how far ahead each widget should look. Saved instantly site-wide.</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {FIELDS.map(section => (
            <div key={section.section} className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 border-b pb-1.5">{section.section}</h3>
              {section.items.map(({ key, label, help }) => {
                const value = cfg[key];
                const isPreset = PRESETS.includes(value);
                return (
                  <div key={key} className="flex flex-wrap items-center gap-3 py-1.5">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="text-sm font-medium">{label}</Label>
                      <p className="text-xs text-gray-500 leading-snug mt-0.5">{help}</p>
                    </div>
                    <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5" data-testid={`vw-presets-${key}`}>
                      {PRESETS.map(p => (
                        <button
                          key={p}
                          type="button"
                          disabled={isViewer}
                          onClick={() => setField(key, p)}
                          data-testid={`vw-preset-${key}-${p}`}
                          className={`px-3 py-1 text-xs font-medium rounded transition ${
                            value === p ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-700 hover:bg-white'
                          }`}
                        >
                          {p}d
                        </button>
                      ))}
                      <div className="flex items-center gap-1 pl-1">
                        <button
                          type="button"
                          disabled={isViewer}
                          onClick={() => setField(key, isPreset ? '' : value)}
                          className={`px-3 py-1 text-xs font-medium rounded transition ${
                            !isPreset ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-700 hover:bg-white'
                          }`}
                          data-testid={`vw-preset-${key}-custom`}
                        >
                          Custom
                        </button>
                        <Input
                          type="number"
                          min={1}
                          max={3650}
                          disabled={isViewer || isPreset}
                          value={isPreset ? '' : value}
                          onChange={(e) => setField(key, e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 0))}
                          placeholder="days"
                          className="w-20 h-8 text-sm"
                          data-testid={`vw-custom-${key}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={resetToDefaults}
              disabled={isViewer || saving}
              data-testid="vw-reset-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset to defaults
            </Button>
            <Button
              onClick={save}
              disabled={isViewer || saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="vw-save-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>
              ) : (
                <><Save className="h-4 w-4 mr-1.5" /> Save Changes</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewWindowsTab;
